import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ContentContext } from "../contexts/ContentContext";
import { FiArrowRight } from "react-icons/fi";
import BrandMark, { BrandWave } from "./BrandMark";

// Self-hosted hero imagery — uses one of the brand's own photos so the
// site reads as a real operation, not a stock-photo template.
const HERO_IMAGE = "/images/date_warehouse.jpg";
const HERO_IMAGE_FALLBACK = "/images/date_farm.jpg";

export default function HeroSection() {
    const { data } = useContext(ContentContext);
    const h = data.hero;

    return (
        <section className="relative overflow-hidden gradient-navy text-white pt-32 pb-32 md:pt-40 md:pb-40">
            {/* One pattern overlay – no orb cloud (per design critique #26) */}
            <div className="absolute inset-0 brand-pattern-light opacity-30 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* Copy column */}
                    <div className="lg:col-span-7 fade-in">
                        <div className="inline-flex items-center gap-3 mb-6 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs md:text-sm font-medium text-gold-200 tracking-wider">
                            <span className="w-1.5 h-1.5 rounded-full bg-gold-300 motion-safe:animate-shimmer" />
                            {h.eyebrow}
                        </div>

                        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6 text-white">
                            {h.title}{" "}
                            <span className="gradient-gold-text">{h.titleAccent}</span>
                        </h1>

                        <p className="text-lg md:text-xl text-white/85 mb-10 leading-relaxed max-w-2xl">
                            {h.subtitle}
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <Link
                                to="/products"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-gold-400 hover:bg-gold-300 text-navy-900 font-semibold rounded-lg transition shadow-soft hover:shadow-glow"
                            >
                                {h.ctaPrimary}
                                <FiArrowRight className="rtl:rotate-180" />
                            </Link>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-lg transition"
                            >
                                {h.ctaSecondary}
                            </Link>
                        </div>
                    </div>

                    {/* Single focal image — no rotated chip (per critique #25) */}
                    <div className="lg:col-span-5 hidden lg:flex justify-end items-center relative">
                        <div className="relative w-full max-w-md aspect-[4/5]">
                            <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-glow border border-white/15">
                                <img
                                    src={HERO_IMAGE}
                                    alt="North Star LLC Bishkek warehouse"
                                    width="800"
                                    height="1000"
                                    loading="eager"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        if (e.currentTarget.src.indexOf(HERO_IMAGE_FALLBACK) === -1) {
                                            e.currentTarget.src = HERO_IMAGE_FALLBACK;
                                        }
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/85 via-brand-deep/15 to-transparent" />
                                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-xl p-3 shadow-soft">
                                    <BrandMark className="w-12 h-12" />
                                </div>
                                <div className="absolute bottom-5 left-5 right-5">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold-300 font-semibold mb-1">
                                        Bishkek · Since 2005
                                    </div>
                                    <div className="font-serif text-xl text-white leading-tight">
                                        Saudi dates, distributed across Eurasia.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats strip — non-clickable, no fake hover state */}
                <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {data.stats.map((s, i) => (
                        <div
                            key={i}
                            className="bg-white/5 backdrop-blur-sm border border-white/15 rounded-2xl p-5 md:p-6"
                        >
                            <div className="text-3xl md:text-4xl font-bold gradient-gold-text font-serif">
                                {s.value}
                            </div>
                            <div className="text-xs md:text-sm text-white/70 mt-1 uppercase tracking-wider">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Brand wave divider into the next section */}
            <div className="absolute bottom-0 inset-x-0 pointer-events-none">
                <BrandWave className="w-full h-20" tone="leaf" />
            </div>
        </section>
    );
}
